// MIT License

// Copyright (c) 2018 LawnStarter

// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:

// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.

// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

// https://github.com/lawnstarter/react-native-picker-select

import React, { PureComponent } from "react";
import {
	Modal,
	Picker,
	Platform,
	StyleSheet,
	ColorPropType,
	Text,
	TextInput,
	TouchableOpacity,
	TouchableWithoutFeedback,
	View,
} from "react-native";
import PropTypes from "prop-types";
import isEqual from "lodash.isequal";

import Textfield from "components/Textfield";

function handlePlaceholder({ placeholder }) {
	if (isEqual(placeholder, {})) {
		return [];
	}
	return [placeholder];
}

function getSelectedItem({ items, value }) {
	return (
		items.find(item => {
			return isEqual(item.value, value);
		}) || items[0]
	);
}

export default class RNPickerSelect extends PureComponent {
	static getDerivedStateFromProps(nextProps, prevState) {
		// update items if items prop changes
		const itemsChanged = !isEqual(prevState.items, nextProps.items);
		// update selectedItem if value prop is defined and differs from currently selected item
		const newSelectedItem = getSelectedItem({
			items: nextProps.items,
			value: nextProps.value,
		});
		const selectedItemChanged =
			!isEqual(nextProps.value, undefined) &&
			!isEqual(prevState.selectedItem, newSelectedItem);

		if (itemsChanged || selectedItemChanged) {
			return {
				items: itemsChanged
					? nextProps.items
					: prevState.items,
				selectedItem: selectedItemChanged
					? newSelectedItem
					: prevState.selectedItem,
			};
		}

		return null;
	}

	constructor(props) {
		super(props);
		
		this.state = {
			items: props.items,
			selectedItem: getSelectedItem({ items: props.items, value: props.value }),
			showPicker: false,
			animationType: undefined,
		};

		this.onUpArrow = this.onUpArrow.bind(this);
		this.onDownArrow = this.onDownArrow.bind(this);
		this.onValueChange = this.onValueChange.bind(this);
		this.togglePicker = this.togglePicker.bind(this);
		this.renderAccessory = this.renderAccessory.bind(this);
	}

	onUpArrow() {
		this.togglePicker();
		setTimeout(() => {
			this.props.onUpArrow();
		});
	}

	onDownArrow() {
		this.togglePicker();
		setTimeout(() => {
			this.props.onDownArrow();
		});
	}

	onValueChange(value, index) {
		this.props.onValueChange(value, index);

		this.setState({
			selectedItem: this.state.items[index],
		});
	}

	togglePicker(animate = false) {
		if (this.props.disabled) {
			return;
		}
		this.setState({
			animationType: animate ? this.props.animationType : undefined,
			showPicker: !this.state.showPicker,
		});
		if (!this.state.showPicker && this.inputRef) {
			this.inputRef.focus();
			this.inputRef.blur();
		}
	}

	renderPickerItems() {
		return this.state.items.map(item => {
			return (
				<Picker.Item
					label={item.label}
					value={item.value}
					key={item.key || item.label}
					color={item.color}
				/>
			);
		});
	}

	renderPlaceholderStyle() {
		const styleModifiers = {};
		if (
			!isEqual(this.props.placeholder, {}) &&
			this.state.selectedItem.label === this.props.placeholder.label
		) {
			styleModifiers.color =
				this.props.style.placeholderColor || "#C7C7CD";
		}
		return styleModifiers;
	}

	renderDoneBar() {
		if (this.props.hideDoneBar) {
			return null;
		}

		return (
			<View
				style={[
					styles.modalViewMiddle,
					this.props.style.modalViewMiddle,
				]}
			>
				<TouchableWithoutFeedback
					onPress={() => {
						this.togglePicker(true);
					}}
					hitSlop={{ top: 2, right: 2, bottom: 2, left: 2 }}
				>
					<View>
						<Text style={[styles.done, this.props.style.done]}>
							{this.props.doneText}
						</Text>
					</View>
				</TouchableWithoutFeedback>
			</View>
		);
	}

	renderIcon() {
		if (this.props.hideIcon) {
			return null;
		}

		return <View style={[styles.icon, this.props.style.icon]} />;
	}

	renderTextInputOrChildren() {
		if (this.props.children) {
			return (
				<View
					pointerEvents="box-only"
					style={this.props.style.inputIOSContainer}
				>
					{this.props.children}
				</View>
			);
		}
		return (
			<View pointerEvents="box-only">
				<Textfield
					value={this.state.selectedItem.label}
					editable={false}
					onChangeText={undefined}
					renderAccessory={this.renderAccessory()}
				/>
				{this.renderIcon()}
			</View>
		);
	}

	renderIOS() {
		return (
			<View
				style={[styles.viewContainer, this.props.style.viewContainer]}
			>
				<TouchableWithoutFeedback
					onPress={() => {
						this.togglePicker(true);
					}}
				>
					{this.renderTextInputOrChildren()}
				</TouchableWithoutFeedback>
				<Modal
					visible={this.state.showPicker}
					transparent
					animationType={this.state.animationType}
					supportedOrientations={["portrait", "landscape"]}
				>
					<TouchableOpacity
						style={[
							styles.modalViewTop,
							this.props.style.modalViewTop,
						]}
						onPress={() => {
							this.togglePicker(true);
						}}
					/>
					{this.renderDoneBar()}
					<View
						style={[
							styles.modalViewBottom,
							this.props.style.modalViewBottom,
						]}
					>
						<Picker
							onValueChange={this.onValueChange}
							selectedValue={this.state.selectedItem.value}
							testId="RNPickerSelectIOS"
						>
							{this.renderPickerItems()}
						</Picker>
					</View>
				</Modal>
			</View>
		);
	}

	renderAndroidHeadless() {
		return (
			<View style={[styles.androidContainer]}>
				{this.props.children}
				<Picker
					style={{
						position: "absolute",
						top: 0,
						width: 1000,
						height: 1000,
					}}
					onValueChange={this.onValueChange}
					selectedValue={this.state.selectedItem.value}
					testId="RNPickerSelectAndroid"
					mode={this.props.mode}
					enabled={!this.props.disabled}
				>
					{this.renderPickerItems()}
				</Picker>
			</View>
		);
	}

	renderAccessory() {
		let { baseColor: backgroundColor } = this.props;
		let triangleStyle = { backgroundColor };

		return (
			<View style={styles.accessory}>
				<View style={styles.triangleContainer}>
					<View style={[styles.triangle, triangleStyle]} />
				</View>
			</View>
		);
	}

	renderAndroid() {
		if (this.props.children) {
			return this.renderAndroidHeadless();
		}

		return (
			<View style={[styles.androidContainer]}>
				<Picker
					onValueChange={this.onValueChange}
					selectedValue={this.state.selectedItem.value}
					testId="RNPickerSelectAndroid"
					mode={this.props.mode}
					style={{ height: 32 }}
					enabled={!this.props.disabled}
				>
					{this.renderPickerItems()}
				</Picker>
			</View>
		);
	}

	render() {
		return (
			<View>
				{this.props.label ? (
					<Text style={[styles.label]}>{this.props.label}</Text>
				) : (
					<View style={[styles.label]} />
				)}
				{Platform.OS === "ios"
					? this.renderIOS()
					: this.renderAndroid()}
			</View>
		);
	}
}

RNPickerSelect.propTypes = {
	onValueChange: PropTypes.func.isRequired,
	items: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired,
			value: PropTypes.any.isRequired,
			key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			color: ColorPropType,
		})
	).isRequired,
	placeholder: PropTypes.shape({
		label: PropTypes.string,
		value: PropTypes.any,
	}),
	hideDoneBar: PropTypes.bool,
	hideIcon: PropTypes.bool,
	disabled: PropTypes.bool,
	value: PropTypes.any, // eslint-disable-line react/forbid-prop-types
	style: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	children: PropTypes.any, // eslint-disable-line react/forbid-prop-types
	mode: PropTypes.string,
	animationType: PropTypes.string,
	onUpArrow: PropTypes.func,
	onDownArrow: PropTypes.func,
	doneText: PropTypes.string,
};

RNPickerSelect.defaultProps = {
	placeholder: {
		label: "Select an item...",
		value: null,
	},
	hideDoneBar: false,
	hideIcon: false,
	disabled: false,
	value: undefined,
	style: {},
	children: null,
	mode: "dialog",
	animationType: "slide",
	onUpArrow: null,
	onDownArrow: null,
	doneText: "Done",
};

const styles = StyleSheet.create({
	androidContainer: {
		flex: 1,
		padding: 5,
		borderWidth: 1,
		borderRadius: 1,
		borderColor: "rgba(0,0,0,0.2)",
		height: 42,
		marginBottom: 20,
	},
	viewContainer: {
		alignSelf: 'stretch',
	},
	chevron: {
		width: 15,
		height: 15,
		backgroundColor: "transparent",
		borderTopWidth: 1.5,
		borderTopColor: "#D0D4DB",
		borderRightWidth: 1.5,
		borderRightColor: "#D0D4DB",
	},
	chevronUp: {
		transform: [{ translateY: 17 }, { rotate: "-45deg" }],
	},
	chevronDown: {
		transform: [{ translateY: 8 }, { rotate: "135deg" }],
	},
	chevronActive: {
		borderTopColor: "#007AFE",
		borderRightColor: "#007AFE",
	},
	icon: {
		position: "absolute",
		backgroundColor: "transparent",
		borderTopWidth: 5,
		borderTopColor: "gray",
		borderRightWidth: 5,
		borderRightColor: "transparent",
		borderLeftWidth: 5,
		borderLeftColor: "transparent",
		width: 0,
		height: 0,
		top: 30,
		right: 20,
	},
	modalViewTop: {
		flex: 1,
	},
	modalViewMiddle: {
		height: 44,
		zIndex: 2,
		flexDirection: "row",
		justifyContent: "flex-end",
		backgroundColor: "#EFF1F2",
	},
	modalViewBottom: {
		height: 215,
		justifyContent: "center",
		backgroundColor: "#D0D4DB",
	},
	done: {
		color: "#007AFE",
		fontWeight: "bold",
		padding: 10,
		fontSize: 18,
	},
	underline: {
		borderTopWidth: 1,
		borderTopColor: "#888988",
		marginHorizontal: 4,
	},
	accessory: {
		width: 24,
		height: 24,
		justifyContent: "center",
		alignItems: "center",
	},

	triangle: {
		width: 8,
		height: 8,
		transform: [
			{
				translateY: -4,
			},
			{
				rotate: "45deg",
			},
		],
	},

	triangleContainer: {
		width: 12,
		height: 6,
		overflow: "hidden",
		alignItems: "center",

		backgroundColor: "transparent" /* XXX: Required */,
	},

	picker: {
		backgroundColor: "rgb(255, 255, 255)",
		borderRadius: 2,

		position: "absolute",

		...Platform.select({
			ios: {
				shadowRadius: 2,
				shadowColor: "rgba(0, 0, 0, 1.0)",
				shadowOpacity: 0.54,
				shadowOffset: { width: 0, height: 2 },
			},

			android: {
				elevation: 2,
			},
		}),
	},
	label: {
		color: "gray",
		marginBottom: Platform.OS == "android" ? 10 : 0,
	},
	scroll: {
		flex: 1,
		borderRadius: 2,
	},

	scrollContainer: {
		paddingVertical: 8,
	},
});



// WEBPACK FOOTER //
// ./src/components/third-party/react-native-picker-select/index.js